/** @jsxRuntime classic */
/** @jsx jsx */

import { css, jsx } from '@emotion/react';
import { labelStyles } from '@zapier/style-encapsulation';
import { Colors, ColorValue, Typography } from '@zapier/design-system';

import {
  BackgroundColorKey,
  FontColor,
  BACKGROUND_TO_FONT_COLORS,
  COLOR_KEYS,
  MEDIA_MIN_WIDTH,
} from '../../constants';
import { RichTextHtml } from '../RichTextHtml/RichTextHtml';
import { RichTextHTMLString } from '../sections/types';
import { ReactNode } from 'react';

type Props = {
  backgroundColor?: BackgroundColorKey;
  children?: ReactNode;
  description: RichTextHTMLString;
  heading: string;
  subHeading?: string;
};

const Styles = labelStyles('Card', {
  root: (backgroundColor: ColorValue) =>
    css({
      padding: '40px 20px',
      backgroundColor,
      border: `1px solid ${Colors.neutral800}`,
      borderRadius: '5px',
      boxShadow: `6px 6px 0 ${Colors.neutral800}`,
      [MEDIA_MIN_WIDTH.small]: {
        padding: '40px',
      },
      [MEDIA_MIN_WIDTH.large]: {
        padding: '44px',
      },
    }),

  heading: (color: FontColor) =>
    css({
      ...Typography.pageHeader9,
      color,
      marginBottom: '8px',
      [MEDIA_MIN_WIDTH.small]: {
        ...Typography.pageHeader7,
      },
      [MEDIA_MIN_WIDTH.large]: {
        ...Typography.pageHeader5,
      },
    }),
  subHeading: (
    color: typeof Colors.zapier | typeof BACKGROUND_TO_FONT_COLORS.zapier,
  ) =>
    css({
      ...Typography.subHeader5Bold,
      fontWeight: 600,
      color,
      marginBottom: '20px',
      [MEDIA_MIN_WIDTH.small]: {
        ...Typography.subHeader3Bold,
      },
      [MEDIA_MIN_WIDTH.large]: {
        marginBottom: '40px',
        ...Typography.subHeader1Bold,
      },
    }),
  description: (color: FontColor) =>
    css({
      color,
      ...Typography.paragraph3,
      [MEDIA_MIN_WIDTH.large]: {
        minHeight: '84px',
        ...Typography.paragraph1Short,
      },
    }),
});

export function Card({
  backgroundColor = COLOR_KEYS.neutral100,
  children,
  description,
  heading,
  subHeading,
}: Props) {
  return (
    <div css={Styles.root(Colors[backgroundColor])}>
      <h3 css={Styles.heading(BACKGROUND_TO_FONT_COLORS[backgroundColor])}>
        {heading}
      </h3>
      {subHeading && (
        <h4
          css={Styles.subHeading(
            backgroundColor === COLOR_KEYS.zapier
              ? BACKGROUND_TO_FONT_COLORS.zapier
              : Colors.zapier,
          )}
        >
          {subHeading}
        </h4>
      )}
      <div css={Styles.description(BACKGROUND_TO_FONT_COLORS[backgroundColor])}>
        <RichTextHtml html={description} />
      </div>
      {children}
    </div>
  );
}
